import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { UseFormReturn } from "react-hook-form";
import useMultiCollapse from "../../../../Common/hooks/useMultiCollapse";
import { ProjectFormType } from "../../../../Common/hooks/useCreateProject";
import styles from "./ProjectUsersTable.module.scss";
import ProjectUserTableRow from "../../../../Common/components/ProjectUserTableRow/ProjectUserTableRow";
import { ToolCapability, User } from "../../../../Common/models/FormModels";
import { Roles } from "../../../../Common/models/roles";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  FetchDataRequest,
  FetchDataResult,
} from "../../../../Common/models/grid";
import { getProjectUsersTableColumns } from "./getProjectUsersTableColumns";
import { Order } from "../../api/models/ProjectUsersTable";
import { TableSortFilterForPagination } from "../../api/models/Table";
import { EntityState } from "../../../../Common/api/models/EntityState";
import { ProjectTool } from "../../../../Common/api/models/ProjectTool";
import { SettingsKeys } from "../../../../Common/models/storageSettingsKeys";
import { mapBackendRolesToDisplay } from "../../../../Common/utils/roleMapping";
import {
  AdvancedFilterOptions,
  hasActiveAdvancedFilters,
} from "../../../../Common/models/advancedFilters";
interface ProjectUsersTableProps {
  fetchTableData: (
    projectId: string,
    request: FetchDataRequest,
    advancedFilters?: AdvancedFilterOptions
  ) => Promise<FetchDataResult>;
  form: UseFormReturn<ProjectFormType>;
  handleDelete: (index: number) => void;
  projectId: string;
  packageId: string;
  useCustomRoleMapping: boolean;
  searchValue: string;
  tableSortFilterForPagination: TableSortFilterForPagination;
  totalRows: number;
  setTotalRows: Dispatch<SetStateAction<number>>;
  handleRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof User
  ) => void;
  handleChangePage: (event: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  userCanEdit: boolean;
  projectTools: ProjectTool[];
  advancedFilters?: AdvancedFilterOptions;
}

const ProjectUsersTable = ({
  projectId,
  packageId,
  useCustomRoleMapping,
  userCanEdit,
  form,
  handleDelete,
  searchValue,
  fetchTableData,
  tableSortFilterForPagination,
  totalRows,
  setTotalRows,
  handleRequestSort,
  handleChangePage,
  handleChangeRowsPerPage,
  projectTools,
  advancedFilters,
}: ProjectUsersTableProps): JSX.Element => {
  const [isLoading, setIsLoading] = useState(false);

  const { ToggleCollapseButton, ToggleItemButton, itemsCollapse, reloadItems } =
    useMultiCollapse(
      () => form.getValues("users").map((_, i) => i),
      false,
      SettingsKeys.ProjectUsersExpanded
    );

  const columns = getProjectUsersTableColumns();
  const users = form.getValues("users");
  const hasActiveSearch = searchValue.trim().length > 0;
  const hasActiveFilters = advancedFilters
    ? hasActiveAdvancedFilters(advancedFilters)
    : false;
  const showNoUsersFoundMessage =
    !isLoading && users.length === 0 && (hasActiveSearch || hasActiveFilters);

  const fetchData = useCallback(async () => {
    setIsLoading(true);

    const { rows, totalRows } = await fetchTableData(projectId, {
      pagination: {
        pageNumber: tableSortFilterForPagination.page,
        pageSize: tableSortFilterForPagination.rowsPerPage,
      },
      sorting: {
        field: tableSortFilterForPagination.orderBy,
        sort: tableSortFilterForPagination.order,
      },
      search: searchValue,
    }, advancedFilters);

    const users = rows.map((u: User) => ({
      ...u,
      editStatus: EntityState.NotChanged,
      // Apply reverse role mapping based on package useCustomRoleMapping flag
      roles: useCustomRoleMapping ? mapBackendRolesToDisplay(u.roles || [], packageId) as Roles[] : u.roles,
      toolCaps: u.toolCaps?.map((tc: ToolCapability) => ({
        ...tc,
        toolCreationCompleted: getToolCreationCompleted(
          tc.toolId,
          tc.toolProjectId
        ),
      })),
    }));
    form.reset({
      users: users,
    } as ProjectFormType);
    setTotalRows(totalRows);
    reloadItems();
    setIsLoading(false);
  }, [tableSortFilterForPagination, searchValue, fetchTableData, advancedFilters]);

  useEffect(() => {
    fetchData();
  }, [tableSortFilterForPagination, searchValue, advancedFilters]);

  const getToolCreationCompleted = (toolId: string, toolProjectId: string) =>
    !!toolProjectId ||
    projectTools.find((pt) => pt.toolId === toolId)?.isManual;

  interface EnhancedTableProps {
    onRequestSort: (
      event: React.MouseEvent<unknown>,
      property: keyof User
    ) => void;
    order: Order;
    orderBy: string;
    rowCount: number;
  }

  function EnhancedTableHead(props: EnhancedTableProps) {
    const { order, orderBy, onRequestSort } = props;
    const createSortHandler =
      (property: keyof User) => (event: React.MouseEvent<unknown>) => {
        onRequestSort(event, property);
      };

    return (
      <TableHead>
        <TableRow>
          {columns.map((headCell) => (
            <TableCell
              sx={{ width: headCell.width }}
              key={headCell.id}
              align={headCell.numeric ? "right" : "left"}
              padding={headCell.disablePadding ? "none" : "normal"}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              {headCell.disableSorting ? (
                <b>{headCell.label}</b>
              ) : (
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={createSortHandler(headCell.id)}
                >
                  <b>{headCell.label}</b>
                </TableSortLabel>
              )}
            </TableCell>
          ))}
          <TableCell>
            <div className={styles.userDetails__collapseHeaderCell}>
              <div className={styles.userDetails__tableHeader}></div>
              <ToggleCollapseButton />
            </div>
          </TableCell>
        </TableRow>
      </TableHead>
    );
  }

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{ width: "92%", pb: 5, paddingBottom: 0, marginBottom: "70px" }}
      >
        <Table>
          <EnhancedTableHead
            order={tableSortFilterForPagination.order}
            orderBy={tableSortFilterForPagination.orderBy}
            onRequestSort={handleRequestSort}
            rowCount={form.getValues("users").length}
          />
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center">
                  <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
                    <CircularProgress size={28} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : showNoUsersFoundMessage ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center">
                  No users found for selected filters
                </TableCell>
              </TableRow>
            ) : (
              users.map((userInProject: User, i) => {
                const userOpen = itemsCollapse[i];
                return (
                  <ProjectUserTableRow
                    form={form}
                    handleDelete={handleDelete}
                    rowIndex={i}
                    user={userInProject}
                    userCanEdit={userCanEdit}
                    userOpen={userOpen}
                    packageId={packageId}
                    useCustomRoleMapping={useCustomRoleMapping}
                    key={i}
                    ToggleItemButton={<ToggleItemButton index={i} />}
                  />
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalRows}
          rowsPerPage={tableSortFilterForPagination.rowsPerPage}
          page={tableSortFilterForPagination.page - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </>
  );
};

export default ProjectUsersTable;
